/**
 * Crossfades the hero product photos and keeps the dots in sync.
 *
 * Autoplay pauses on hover, on keyboard focus and while the tab is hidden, and
 * never starts at all when the visitor asks for reduced motion — the slides
 * still work as a manual, dot-driven gallery in that case.
 */
class CkHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.ck-hero__slide'));
    if (this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('.ck-hero__dot'));
    this.index = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.interval = Number(this.dataset.interval) || 5000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.show(i);
        this.start();
      });
    });

    this.addEventListener('mouseenter', this.stop);
    this.addEventListener('mouseleave', this.start);
    this.addEventListener('focusin', this.stop);
    this.addEventListener('focusout', this.start);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Waiting for the page to fully load before the first rotation keeps the
    // first slide's fade-out from invalidating it as the Largest Contentful
    // Paint candidate while the page is still being measured/loaded.
    if (document.readyState === 'complete') {
      this.start();
    } else {
      window.addEventListener('load', this.start, { once: true });
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange = () => {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  };

  show(next) {
    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('inert', '');
    if (this.dots[this.index]) {
      this.dots[this.index].classList.remove('is-active');
      this.dots[this.index].removeAttribute('aria-current');
    }

    this.index = (next + this.slides.length) % this.slides.length;

    this.slides[this.index].classList.add('is-active');
    this.slides[this.index].removeAttribute('inert');
    if (this.dots[this.index]) {
      this.dots[this.index].classList.add('is-active');
      this.dots[this.index].setAttribute('aria-current', 'true');
    }
  }

  start = () => {
    this.stop();
    if (this.dataset.autoplay === 'false' || this.reduceMotion) return;
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
  };

  stop = () => {
    clearInterval(this.timer);
    this.timer = null;
  };
}

if (!customElements.get('ck-hero-slideshow')) {
  customElements.define('ck-hero-slideshow', CkHeroSlideshow);
}
